import { removeRightWhile } from "@aprao/common/src/array/removeRightWhile.js";
import type { BigFraction } from "@aprao/common/src/math/BigFraction.js";
import { RtPositiveInteger } from "@aprao/common/src/runtypes/common.js";
import type { PositiveInteger } from "@aprao/common/src/shapes/common.js";
import { times } from "@aprao/common/src/util/times.js";
import { makeAutoObservable, reaction } from "mobx";
import { createErr, createOk } from "option-t/plain_result";

import { MAX_PERIOD_COUNT, RtPeriodIndex } from "../delegates/utils.js";

interface SpreadByRateParams {
  // allow to use nominal typing to mark validated parameters
  __spreadByRateParams: true;
  periodIndex: number;
  rate: number;
}

/**
 * Sets a value at the given index while expanding it if necessary.
 */
const setCountAtPeriodIndex = (
  periods: Array<PositiveInteger | null>,
  periodIndex: number,
  count: number | null,
) => {
  if (periods.length <= periodIndex) {
    periods.push(...times(periodIndex - periods.length + 1, () => null));
  }

  periods[periodIndex] = count as PositiveInteger | null;
};

/**
 * Traverses the periods' array to cap the unit spread to the given maximum.
 *
 * The idea is that we can adjust the periods array's values freely and
 * normalise it just after.
 *
 * For example if we have a max count of 3:
 * - we start with `[1, 1, null, 1]`
 * - adjust index 2 to get `[1, 5, null, 1]`
 * - normalise to `[1, 2, null, null]`
 */
const normalizePeriodsSumCount = (
  periods: Array<PositiveInteger | null>,
  maxCount: number,
) => {
  for (let periodIndex = 0; periodIndex < periods.length; periodIndex++) {
    const leftoverCount = Math.min(
      maxCount,
      periods[periodIndex] ?? 0,
    ) as PositiveInteger;

    periods[periodIndex] = leftoverCount === 0 ? null : leftoverCount;

    maxCount -= leftoverCount;
  }
};

/**
 * Trim a potentially empty (null or undefined value) tail.
 */
const trimTail = (periods: Array<PositiveInteger | null>) =>
  removeRightWhile(periods, (count) => count != null);

export const computeProjectionAmount = (
  projectionPeriods: SalesProjectionPeriodsDelegate,
  amountMean: BigFraction | null,
  periodIndex: number,
) => {
  const count = projectionPeriods.getCountAtPeriodIndex(periodIndex);
  return count == null || amountMean == null ? null : amountMean.mul(count);
};

export class SalesProjectionPeriodsDelegate {
  readonly _providers;

  constructor(providers: {
    totalCount: () => PositiveInteger;
    periods: () => Array<PositiveInteger | null>;
  }) {
    makeAutoObservable(this);

    this._providers = providers;

    /**
     * This code runs automatically after a total count update to ensure periods
     * array is normalised to account for a potential reduction of the number of
     * unit assignable.
     */
    reaction(
      () => this._providers.totalCount() as number,
      (totalCount) => {
        normalizePeriodsSumCount(this._providers.periods(), totalCount);
        trimTail(this._providers.periods());
      },
    );
  }

  get duration() {
    return this._providers.periods().length;
  }

  get assignableCount() {
    return this.assignableCountFromPeriodIndex(Number.POSITIVE_INFINITY);
  }

  assignableCountFromPeriodIndex = (periodIndex: number) =>
    this._providers
      .periods()
      .slice(0, periodIndex)
      .reduce(
        (sum, count) => (count == null ? sum : sum - count),
        this._providers.totalCount() as number,
      );

  getCountAtPeriodIndex = (periodIndex: number) => {
    const periods = this._providers.periods();

    if (periods.length <= periodIndex) {
      return null;
    }

    return periods[periodIndex] ?? null;
  };

  /**
   * Assigns a `count` of units to the given period.
   *
   * It adjusts the trailing units assignments to satisfy the request while
   * respecting the total count reducing the amount of units assigned to the
   * last periods.
   *
   * @param periodIndex the target period
   * @param count the number of units to assigned to the given period
   */
  adjustPeriod(periodIndex: number, count: number | null) {
    if (count == null) {
      setCountAtPeriodIndex(this._providers.periods(), periodIndex, null);
    } else {
      const countAdjustedForSpot = Math.min(
        this.assignableCountFromPeriodIndex(periodIndex),
        count,
      );

      setCountAtPeriodIndex(
        this._providers.periods(),
        periodIndex,
        countAdjustedForSpot,
      );

      normalizePeriodsSumCount(
        this._providers.periods(),
        this._providers.totalCount(),
      );
    }

    trimTail(this._providers.periods());
  }

  /**
   * Checks the spread parameters coherence and return the params to give to
   * {@link #spreadByRate}.
   */
  validateSpreadByRate(params: { periodIndex: number; rate: number }) {
    const parsedPeriodIndex = RtPeriodIndex.safeParse(params.periodIndex);
    const parsedRate = RtPositiveInteger.safeParse(params.rate);

    if (
      parsedPeriodIndex.success &&
      parsedRate.success &&
      params.periodIndex + this._providers.totalCount() / params.rate <=
        MAX_PERIOD_COUNT
    ) {
      return createOk<SpreadByRateParams>({
        __spreadByRateParams: true,
        periodIndex: parsedPeriodIndex.data,
        rate: parsedRate.data,
      } as const);
    } else {
      return createErr("Invalid spread parameters.");
    }
  }

  /**
   * Assigns units following the `rate` from the given start period until no units
   * are available to assign.
   *
   * It replaces the current schedule so no attempt is made to adjust existing
   * assignments.
   */
  spreadByRate(params: SpreadByRateParams) {
    const periods = this._providers.periods();

    // clear the current period array
    periods.length = 0;

    let leftoverCount = this._providers.totalCount() as number;
    for (
      let periodIndex = params.periodIndex;
      0 < leftoverCount;
      periodIndex++
    ) {
      const count = Math.min(leftoverCount, params.rate);
      setCountAtPeriodIndex(periods, periodIndex, count);
      leftoverCount = leftoverCount - count;
    }
  }
}
